import type {
  Camera,
  HistoryRecord,
  ChartDataPoint,
  CameraState,
  SessionState,
  CameraDisplayState,
  SessionDisplayState,
} from '@smolt/shared/types';
import {
  mockCameras,
  generateChartData,
  generateIncrementalChartData,
  generateHistoryRecords,
  getHistoryChartData,
  saveHistoryChartData,
} from './mock-data';

// Simulate network delay
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Real-time counting session management
 */
interface CountingSession {
  startTime: number;
  lastCount: number;
  lastUpdateTime: number;
  historyRecordKey?: string; // Key of the active history record being updated
}

const countingSessions = new Map<string, CountingSession>();

// History records per camera
const historyRecords = new Map<string, HistoryRecord[]>();

// Track active (currently counting) record keys per camera
const activeHistoryRecords = new Map<string, string>();

// Cache last returned camera objects to enable structural sharing
const cameraCache = new Map<string, Camera>();

// Cache chart data for counting sessions to prevent regeneration on each call
interface ChartDataCache {
  data: ChartDataPoint[];
  lastTotalSeconds: number;
  targetCount: number;
}
const chartDataCache = new Map<string, ChartDataCache>();

// Initialize history records for all cameras
const initializeHistoryRecords = () => {
  mockCameras.forEach((camera) => {
    historyRecords.set(camera.id, generateHistoryRecords(120));
  });
};

// Initialize first camera with existing session (already counting)
const initializeFirstCamera = () => {
  const firstCamera = mockCameras[0];
  if (firstCamera && firstCamera.sessionState === 'counting') {
    // Calculate start time based on current duration (01:23:45 = 5025 seconds)
    const durationParts = firstCamera.duration.split(':').map(Number);
    const totalSeconds = durationParts[0] * 3600 + durationParts[1] * 60 + durationParts[2];
    const startTime = Date.now() - totalSeconds * 1000;

    const recordKey = `record-active-${firstCamera.id}`;

    countingSessions.set(firstCamera.id, {
      startTime,
      lastCount: firstCamera.count,
      lastUpdateTime: Date.now(),
      historyRecordKey: recordKey,
    });

    // Create active history record for the counting session
    const dateNow = new Date(startTime);
    const year = dateNow.getFullYear();
    const month = String(dateNow.getMonth() + 1).padStart(2, '0');
    const day = String(dateNow.getDate()).padStart(2, '0');

    const activeRecord: HistoryRecord = {
      key: recordKey,
      time: `${year}-${month}-${day}`,
      // inputTank and outputTank intentionally undefined during active counting
      duration: firstCamera.duration,
      count: firstCamera.count,
      weight: firstCamera.weight,
      kgPerFish: firstCamera.weightPerFish,
    };

    // Insert active record at the beginning of history
    const records = historyRecords.get(firstCamera.id) || [];
    records.unshift(activeRecord);
    historyRecords.set(firstCamera.id, records);

    // Mark as active record
    activeHistoryRecords.set(firstCamera.id, recordKey);

    // Pre-generate chart data for the existing session
    const chartData = generateChartData(totalSeconds, firstCamera.count);
    chartDataCache.set(firstCamera.id, {
      data: chartData,
      lastTotalSeconds: totalSeconds,
      targetCount: firstCamera.count,
    });
  }
};

// Initialize on module load
initializeHistoryRecords();
initializeFirstCamera();

/**
 * Calculate current count based on elapsed time (updates every 0.1 seconds for smooth animation)
 */
const calculateRealtimeCount = (session: CountingSession): number => {
  const now = Date.now();
  const elapsed100ms = Math.floor((now - session.lastUpdateTime) / 100);

  if (elapsed100ms === 0) {
    return session.lastCount;
  }

  // Add random increment for each 0.1 second interval (~102,000 fish/hour target)
  let newCount = session.lastCount;
  for (let i = 0; i < elapsed100ms; i++) {
    // Random increment per 0.1 second (0.1 to 5.57 fish, averaging 2.833 per 0.1s = ~102,000/hr)
    const increment = 0.1 + Math.random() * 5.47;
    newCount += increment;
  }

  // Update session
  session.lastCount = newCount;
  session.lastUpdateTime = now;

  return Math.floor(newCount);
};

/**
 * Format duration from start time
 */
const formatDuration = (startTime: number): string => {
  const elapsed = Date.now() - startTime;
  const totalSeconds = Math.floor(elapsed / 1000);

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

/**
 * State mapping utilities
 */

// Get Camera Display State
export const getCameraDisplayState = (cameraState: CameraState): CameraDisplayState => {
  switch (cameraState) {
    case 'disconnected':
      return 'offline';
    case 'connecting':
      return 'transitioning';
    case 'connected':
      return 'ready';
    case 'playing':
      return 'streaming';
    case 'error':
      return 'transitioning';
    default:
      return 'offline';
  }
};

// Get Session Display State
export const getSessionDisplayState = (sessionState: SessionState): SessionDisplayState => {
  switch (sessionState) {
    case 'idle':
      return 'idle';
    case 'ready':
      return 'idle';
    case 'starting':
      return 'starting';
    case 'counting':
      return 'active';
    case 'stopping':
      return 'stopping';
    case 'error':
      return 'error';
    default:
      return 'idle';
  }
};

// Check if session can start
export const canStartSession = (camera: Camera): boolean => {
  return camera.cameraState === 'playing' && camera.sessionState === 'ready';
};

// Check if all sessions can start
export const canStartAllSessions = (cameras: Camera[]): boolean => {
  return cameras.every(canStartSession) && cameras.length > 0;
};

/**
 * Mock API service that simulates backend responses
 * This service can be easily replaced with real API calls later
 */
export const mockApi = {
  /**
   * Get camera list (ID and name only) - for dashboard
   */
  getCameraList: async (): Promise<Pick<Camera, 'id' | 'name'>[]> => {
    await delay(10);
    return mockCameras.map((camera) => ({
      id: camera.id,
      name: camera.name,
    }));
  },

  /**
   * Get all cameras (legacy - for backward compatibility)
   */
  getCameras: async (): Promise<Camera[]> => {
    await delay(10); // Minimal delay for very fast updates (10ms)

    // Update counting cameras with real-time data
    return mockCameras.map((camera) => {
      const session = countingSessions.get(camera.id);

      if (session && camera.sessionState === 'counting') {
        const currentCount = calculateRealtimeCount(session);
        const duration = formatDuration(session.startTime);
        const weightPerFish = camera.weightPerFish ?? 0;
        const weight = (currentCount * weightPerFish) / 1000; // Convert to tons
        const roundedWeight = parseFloat(weight.toFixed(2));

        // Check cached value to enable React Query structural sharing
        const cached = cameraCache.get(camera.id);
        if (
          cached &&
          cached.count === currentCount &&
          cached.duration === duration &&
          cached.weight === roundedWeight
        ) {
          return cached; // Return same object reference if data unchanged
        }

        const newCamera = {
          ...camera,
          count: currentCount,
          duration,
          weight: roundedWeight,
        };
        cameraCache.set(camera.id, newCamera);
        return newCamera;
      }

      // For non-counting cameras, return cached or original
      const cached = cameraCache.get(camera.id);
      if (cached && cached.sessionState === camera.sessionState) {
        return cached;
      }

      cameraCache.set(camera.id, camera);
      return camera;
    });
  },

  /**
   * Get single camera by ID
   */
  getCameraById: async (id: string): Promise<Camera | null> => {
    await delay(10); // Minimal delay for very fast updates (10ms)
    const camera = mockCameras.find((cam) => cam.id === id);

    if (!camera) return null;

    // Update with real-time data if counting
    const session = countingSessions.get(id);
    if (session && camera.sessionState === 'counting') {
      const currentCount = calculateRealtimeCount(session);
      const duration = formatDuration(session.startTime);
      const weightPerFish = camera.weightPerFish ?? 0;
      const weight = (currentCount * weightPerFish) / 1000;

      return {
        ...camera,
        count: currentCount,
        duration,
        weight: parseFloat(weight.toFixed(2)),
      };
    }

    // Always return new object reference to ensure React Query detects changes
    return { ...camera };
  },

  /**
   * Update camera settings
   */
  updateCameraSettings: async (
    id: string,
    settings: {
      cameraName?: string;
      weightPerFish?: number;
      inputTank?: string;
      outputTank?: string;
    }
  ): Promise<Camera> => {
    await delay(400);
    const camera = mockCameras.find((cam) => cam.id === id);
    if (!camera) {
      throw new Error(`Camera ${id} not found`);
    }

    if (settings.cameraName) camera.name = settings.cameraName;
    if (settings.weightPerFish) camera.weightPerFish = settings.weightPerFish;
    if (settings.inputTank !== undefined) camera.inputTank = settings.inputTank || undefined;
    if (settings.outputTank !== undefined) camera.outputTank = settings.outputTank || undefined;

    return camera;
  },

  /**
   * Start counting session
   */
  startCounting: async (id: string): Promise<Camera> => {
    await delay(500);
    const camera = mockCameras.find((cam) => cam.id === id);
    if (!camera) {
      throw new Error(`Camera ${id} not found`);
    }

    // Reset count to 0
    camera.count = 0;
    camera.weight = 0;
    camera.duration = '00:00:00';
    camera.sessionState = 'starting';

    // Simulate pipeline initialization, then transition to counting
    await delay(500);
    camera.sessionState = 'counting';

    // Initialize counting session
    const now = Date.now();
    const recordKey = `record-${now}`;

    // Clear any existing chart data cache for fresh start
    chartDataCache.delete(id);

    countingSessions.set(id, {
      startTime: now,
      lastCount: 0,
      lastUpdateTime: now,
      historyRecordKey: recordKey,
    });

    // Create initial history record with zero values
    const dateNow = new Date();
    const year = dateNow.getFullYear();
    const month = String(dateNow.getMonth() + 1).padStart(2, '0');
    const day = String(dateNow.getDate()).padStart(2, '0');

    const initialRecord: HistoryRecord = {
      key: recordKey,
      time: `${year}-${month}-${day}`,
      // inputTank and outputTank intentionally undefined during active counting
      duration: '00:00:00',
      count: 0,
      weight: 0,
      kgPerFish: camera.weightPerFish,
    };

    const records = historyRecords.get(id) || [];
    records.unshift(initialRecord);

    // Keep only last 120 records
    if (records.length > 120) {
      records.pop();
    }

    historyRecords.set(id, records);
    activeHistoryRecords.set(id, recordKey);

    return camera;
  },

  /**
   * Stop counting session
   */
  stopCounting: async (id: string): Promise<Camera> => {
    await delay(500);
    const camera = mockCameras.find((cam) => cam.id === id);
    if (!camera) {
      throw new Error(`Camera ${id} not found`);
    }

    // Get final count before stopping
    const session = countingSessions.get(id);
    const activeRecordKey = activeHistoryRecords.get(id) || session?.historyRecordKey;

    // Track the final record key for chart data saving
    let finalRecordKey = activeRecordKey;

    if (session) {
      const finalCount = calculateRealtimeCount(session);
      const finalDuration = formatDuration(session.startTime);
      const weightPerFish = camera.weightPerFish ?? 0;
      const finalWeight = (finalCount * weightPerFish) / 1000;

      camera.count = finalCount;
      camera.duration = finalDuration;
      camera.weight = parseFloat(finalWeight.toFixed(2));

      // Update the active history record with final values (instead of adding a new one)
      const records = historyRecords.get(id) || [];

      if (activeRecordKey) {
        const activeRecordIndex = records.findIndex((r) => r.key === activeRecordKey);

        if (activeRecordIndex !== -1) {
          // Update existing record with final values and tank info
          records[activeRecordIndex] = {
            ...records[activeRecordIndex],
            inputTank: camera.inputTank,
            outputTank: camera.outputTank,
            duration: finalDuration,
            count: finalCount,
            weight: parseFloat(finalWeight.toFixed(2)),
            kgPerFish: camera.weightPerFish,
          };
        } else {
          // Record not found, create a new one at the beginning
          const dateNow = new Date();
          const year = dateNow.getFullYear();
          const month = String(dateNow.getMonth() + 1).padStart(2, '0');
          const day = String(dateNow.getDate()).padStart(2, '0');

          const newRecord: HistoryRecord = {
            key: activeRecordKey,
            time: `${year}-${month}-${day}`,
            inputTank: camera.inputTank,
            outputTank: camera.outputTank,
            duration: finalDuration,
            count: finalCount,
            weight: parseFloat(finalWeight.toFixed(2)),
            kgPerFish: camera.weightPerFish,
          };
          records.unshift(newRecord);
        }
      } else {
        // No active record key, create a new history record
        const dateNow = new Date();
        const year = dateNow.getFullYear();
        const month = String(dateNow.getMonth() + 1).padStart(2, '0');
        const day = String(dateNow.getDate()).padStart(2, '0');
        const newRecordKey = `record-${Date.now()}`;

        const newRecord: HistoryRecord = {
          key: newRecordKey,
          time: `${year}-${month}-${day}`,
          inputTank: camera.inputTank,
          outputTank: camera.outputTank,
          duration: finalDuration,
          count: finalCount,
          weight: parseFloat(finalWeight.toFixed(2)),
          kgPerFish: camera.weightPerFish,
        };
        records.unshift(newRecord);

        // Update finalRecordKey to the newly created key
        finalRecordKey = newRecordKey;
      }

      // Keep only last 120 records
      if (records.length > 120) {
        records.pop();
      }

      historyRecords.set(id, records);

      // Clear active record tracking
      activeHistoryRecords.delete(id);
    }

    camera.sessionState = 'stopping';

    // Save chart data to history cache before cleanup
    const cachedChartData = chartDataCache.get(id);
    if (cachedChartData && finalRecordKey) {
      saveHistoryChartData(finalRecordKey, cachedChartData.data);
    }

    // Simulate data save & cleanup, then transition to ready
    await delay(500);
    camera.sessionState = 'ready';

    // Clean up counting session only
    // Note: Keep chartDataCache so the chart persists after stopping
    // It will be cleared when a new session starts
    countingSessions.delete(id);

    return camera;
  },

  /**
   * Stop all counting sessions
   */
  stopAllCounting: async (): Promise<Camera[]> => {
    await delay(600);

    // Get final counts before stopping
    mockCameras.forEach((camera) => {
      if (camera.sessionState === 'counting' || camera.sessionState === 'starting') {
        const session = countingSessions.get(camera.id);
        const activeRecordKey = activeHistoryRecords.get(camera.id) || session?.historyRecordKey;

        if (session) {
          const finalCount = calculateRealtimeCount(session);
          const finalDuration = formatDuration(session.startTime);
          const weightPerFish = camera.weightPerFish ?? 0;
          const finalWeight = (finalCount * weightPerFish) / 1000;

          camera.count = finalCount;
          camera.duration = finalDuration;
          camera.weight = parseFloat(finalWeight.toFixed(2));

          // Update the active history record with final values
          const records = historyRecords.get(camera.id) || [];

          if (activeRecordKey) {
            const activeRecordIndex = records.findIndex((r) => r.key === activeRecordKey);

            if (activeRecordIndex !== -1) {
              // Update existing record with final values and tank info
              records[activeRecordIndex] = {
                ...records[activeRecordIndex],
                inputTank: camera.inputTank,
                outputTank: camera.outputTank,
                duration: finalDuration,
                count: finalCount,
                weight: parseFloat(finalWeight.toFixed(2)),
                kgPerFish: camera.weightPerFish,
              };
            } else {
              // Record not found, create a new one at the beginning
              const dateNow = new Date();
              const year = dateNow.getFullYear();
              const month = String(dateNow.getMonth() + 1).padStart(2, '0');
              const day = String(dateNow.getDate()).padStart(2, '0');

              const newRecord: HistoryRecord = {
                key: activeRecordKey,
                time: `${year}-${month}-${day}`,
                inputTank: camera.inputTank,
                outputTank: camera.outputTank,
                duration: finalDuration,
                count: finalCount,
                weight: parseFloat(finalWeight.toFixed(2)),
                kgPerFish: camera.weightPerFish,
              };
              records.unshift(newRecord);
            }
          } else {
            // No active record key, create a new history record
            const dateNow = new Date();
            const year = dateNow.getFullYear();
            const month = String(dateNow.getMonth() + 1).padStart(2, '0');
            const day = String(dateNow.getDate()).padStart(2, '0');
            const newRecordKey = `record-${Date.now()}-${camera.id}`;

            const newRecord: HistoryRecord = {
              key: newRecordKey,
              time: `${year}-${month}-${day}`,
              inputTank: camera.inputTank,
              outputTank: camera.outputTank,
              duration: finalDuration,
              count: finalCount,
              weight: parseFloat(finalWeight.toFixed(2)),
              kgPerFish: camera.weightPerFish,
            };
            records.unshift(newRecord);
          }

          // Keep only last 120 records
          if (records.length > 120) {
            records.pop();
          }

          historyRecords.set(camera.id, records);

          // Clear active record tracking
          activeHistoryRecords.delete(camera.id);
        }
        camera.sessionState = 'stopping';
      }
    });

    // Wait for cleanup simulation, then transition to ready
    await delay(500);
    mockCameras.forEach((camera) => {
      if (camera.sessionState === 'stopping') {
        camera.sessionState = 'ready';
        countingSessions.delete(camera.id);
        chartDataCache.delete(camera.id);
      }
    });

    return mockCameras;
  },

  /**
   * Start all counting sessions
   */
  startAllCounting: async (): Promise<Camera[]> => {
    await delay(600);

    // First, set all to starting and reset counts
    mockCameras.forEach((camera) => {
      if (canStartSession(camera)) {
        camera.count = 0;
        camera.weight = 0;
        camera.duration = '00:00:00';
        camera.sessionState = 'starting';
      }
    });

    // Wait for pipeline initialization, then transition to counting
    await delay(500);
    const now = Date.now();
    mockCameras.forEach((camera) => {
      if (camera.sessionState === 'starting') {
        camera.sessionState = 'counting';

        const recordKey = `record-${now}-${camera.id}`;

        // Clear any existing chart data cache for fresh start
        chartDataCache.delete(camera.id);

        // Initialize counting session
        countingSessions.set(camera.id, {
          startTime: now,
          lastCount: 0,
          lastUpdateTime: now,
          historyRecordKey: recordKey,
        });

        // Create initial history record with zero values
        const dateNow = new Date();
        const year = dateNow.getFullYear();
        const month = String(dateNow.getMonth() + 1).padStart(2, '0');
        const day = String(dateNow.getDate()).padStart(2, '0');

        const initialRecord: HistoryRecord = {
          key: recordKey,
          time: `${year}-${month}-${day}`,
          // inputTank and outputTank intentionally undefined during active counting
          duration: '00:00:00',
          count: 0,
          weight: 0,
          kgPerFish: camera.weightPerFish,
        };

        const records = historyRecords.get(camera.id) || [];
        records.unshift(initialRecord);

        // Keep only last 120 records
        if (records.length > 120) {
          records.pop();
        }

        historyRecords.set(camera.id, records);
        activeHistoryRecords.set(camera.id, recordKey);
      }
    });

    return mockCameras;
  },

  /**
   * Get chart data for a camera
   */
  getChartData: async (id: string): Promise<ChartDataPoint[]> => {
    await delay(10);

    const camera = mockCameras.find((cam) => cam.id === id);
    const session = countingSessions.get(id);

    // If counting, use cached chart data with incremental updates
    if (camera && session && camera.sessionState === 'counting') {
      const now = Date.now();
      const elapsed = now - session.startTime;
      const totalSeconds = Math.floor(elapsed / 1000);

      // Get current count for the chart endpoint
      const currentCount = calculateRealtimeCount(session);

      // Check cache - use incremental updates if available
      let cache = chartDataCache.get(id);

      if (cache && cache.data.length > 0) {
        const timeDiff = totalSeconds - cache.lastTotalSeconds;

        // Incremental update: append new data points instead of regenerating
        if (timeDiff > 0 && timeDiff <= 60) {
          // Small time difference: add new points incrementally
          const newPoints = generateIncrementalChartData(
            cache.data,
            cache.lastTotalSeconds,
            totalSeconds,
            currentCount
          );
          if (newPoints.length > 0) {
            cache.data = [...cache.data, ...newPoints];
          }
          cache.lastTotalSeconds = totalSeconds;
          cache.targetCount = currentCount;
        } else if (timeDiff > 60) {
          // Large time gap: regenerate entire chart
          cache.data = generateChartData(totalSeconds, currentCount);
          cache.lastTotalSeconds = totalSeconds;
          cache.targetCount = currentCount;
        }
        // If timeDiff <= 0, keep existing cache (no update needed)
      } else {
        // No cache: generate new chart data
        cache = {
          data: generateChartData(totalSeconds, currentCount),
          lastTotalSeconds: totalSeconds,
          targetCount: currentCount,
        };
      }

      chartDataCache.set(id, cache);

      // Show full session data (no windowing for prototype)
      // This ensures the chart always starts from 00:00
      const maxDataPoints = totalSeconds;
      const startOffset = 0;

      // Filter data to show only the window
      const windowData = cache.data.filter((point: ChartDataPoint) => {
        const [minutes, seconds] = point.time.split(':').map(Number);
        const pointSeconds = minutes * 60 + seconds;
        return pointSeconds >= startOffset && pointSeconds <= totalSeconds;
      });

      // Ensure at least 2 points
      if (windowData.length < 2 && cache.data.length > 0) {
        return cache.data.slice(-Math.max(2, Math.min(cache.data.length, maxDataPoints)));
      }

      return windowData;
    }

    // If not counting, return cached data if available (for just-stopped sessions)
    // This allows the chart to persist briefly after stopping before switching to history view
    const cache = chartDataCache.get(id);
    if (cache && cache.data.length > 0) {
      return cache.data;
    }

    // No cached data, return empty array
    return [];
  },

  /**
   * Get history records for a camera
   */
  getHistoryRecords: async (id: string): Promise<HistoryRecord[]> => {
    await delay(10); // Reduced delay for faster updates
    const records = historyRecords.get(id) || [];

    // If camera is currently counting, update the active record with real-time values
    const session = countingSessions.get(id);
    const activeRecordKey = activeHistoryRecords.get(id);

    if (session && activeRecordKey && records.length > 0 && records[0].key === activeRecordKey) {
      const currentCount = calculateRealtimeCount(session);
      const currentDuration = formatDuration(session.startTime);
      const camera = mockCameras.find((cam) => cam.id === id);
      const weightPerFish = camera?.weightPerFish ?? 0;
      const currentWeight = (currentCount * weightPerFish) / 1000;

      // Update the first record with real-time values
      const updatedRecord = {
        ...records[0],
        duration: currentDuration,
        count: currentCount,
        weight: parseFloat(currentWeight.toFixed(2)),
        kgPerFish: weightPerFish,
        inputTank: camera?.inputTank,
        outputTank: camera?.outputTank,
      };

      // Create new array with updated first record
      const updatedRecords = [updatedRecord, ...records.slice(1)];

      // Save back to the map so next call gets updated values
      historyRecords.set(id, updatedRecords);

      return updatedRecords;
    }

    return records;
  },

  /**
   * Get chart data for a specific history record by recordKey
   */
  getHistoryChartData: async (recordKey: string): Promise<ChartDataPoint[]> => {
    await delay(10);

    // Get cached chart data for this history record
    const cachedData = getHistoryChartData(recordKey);

    return cachedData;
  },
};
